/**
 * Path-based router.
 *
 * ## Why paths and not the fragment
 *
 * The URL fragment is load-bearing elsewhere in this architecture: it is where
 * the normative claim URL carries the claim secret
 * (PHASE_01_PRODUCTION_ARCHITECTURE.md §5), and its capture-and-removal
 * behaviour is exactly what Phase 02 PV-16 is still validating. This router
 * therefore never reads, writes, observes or depends on `location.hash`. A
 * general-purpose fragment reader in the shared frontend would be the wrong
 * thing to have lying around while that validation is open.
 *
 * No route carries a secret, an identifier or any value derived from one.
 *
 * ## Accessibility
 *
 * A client-side navigation that only swaps DOM strands a screen-reader user:
 * focus stays where it was and nothing is announced. Each navigation therefore
 * sets the document title and moves focus to the new page heading, which is the
 * behaviour a full page load would have given for free.
 */

export interface RouteView {
  readonly node: Node;
  /** Release listeners and timers. Called before the next view renders. */
  destroy?(): void;
}

export interface Route {
  readonly path: string;
  readonly title: string;
  /** Content column. Defaults to the reading measure; "wide" suits reference pages. */
  readonly width?: "default" | "wide";
  /**
   * How much of the application shell surrounds this route.
   *
   * `bare` hides the app bar, the demonstration banner and the footer. It exists
   * for one case: a fully white-labelled bank surface rendered as a whole page,
   * where Nura's wordmark sitting above it would be branding on the one screen
   * that must not carry any. The route is responsible for its own disclosure.
   */
  readonly chrome?: "full" | "bare";
  render(navigate: Navigate): RouteView;
}

export type Navigate = (path: string) => void;

export interface RouterOptions {
  readonly routes: readonly Route[];
  readonly outlet: HTMLElement;
  readonly fallback: string;
  readonly titleSuffix: string;
  readonly onNavigate?: (route: Route) => void;
}

export function createRouter(options: RouterOptions): { start(): void; navigate: Navigate } {
  let active: RouteView | undefined;

  function resolve(path: string): Route {
    const match = options.routes.find((route) => route.path === path);
    if (match !== undefined) return match;
    const fallback = options.routes.find((route) => route.path === options.fallback);
    if (fallback === undefined) throw new Error(`no route registered for ${options.fallback}`);
    return fallback;
  }

  function render(path: string): void {
    const route = resolve(path);

    active?.destroy?.();
    const view = route.render(navigate);
    active = view;

    options.outlet.replaceChildren(view.node);
    if (route.width === "wide") options.outlet.setAttribute("data-width", "wide");
    else options.outlet.removeAttribute("data-width");
    document.title = `${route.title} · ${options.titleSuffix}`;

    const heading = options.outlet.querySelector("h1");
    if (heading instanceof HTMLElement) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
    window.scrollTo({ top: 0, behavior: "auto" });

    options.onNavigate?.(route);
  }

  function navigate(path: string): void {
    if (path === window.location.pathname) return;
    window.history.pushState({}, "", path);
    render(path);
  }

  return {
    start(): void {
      window.addEventListener("popstate", () => {
        render(window.location.pathname);
      });
      // Intercept in-app links so navigation is one code path, while external
      // and modified clicks keep their normal browser behaviour.
      document.addEventListener("click", (event) => {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const anchor = (event.target as Element | null)?.closest("a");
        if (!(anchor instanceof HTMLAnchorElement)) return;
        if (anchor.target !== "" && anchor.target !== "_self") return;
        if (anchor.origin !== window.location.origin) return;
        if (anchor.hasAttribute("download")) return;
        event.preventDefault();
        navigate(anchor.pathname);
      });
      render(window.location.pathname);
    },
    navigate,
  };
}
